import { ref, computed } from "vue"
import { useStore } from "vuex"
import NykAlert from "../NykAlert/NykAlert.vue";
import {
    handleNavigateBackInSubmenu,
    handleNavigateForwardInSubmenu,
    handleNavigateToPreviousListInSubmenu,
    handleNavigateToNextListInSubmenu,
    handleCloseSubmenu,
} from "../../foundation/js/menu-key-helper"

export default {
    name: "LoginOverlay",
    components: {
        NykAlert
    },
    props: {
        bannerImageUrl: {
            type: String,
            required: false,
            default: null
        },
        footerItems: {
            type: Array,
            required: true,
        },
        cards: {
            type: Array,
            required: true,
        },
        languageItems: {
            type: Array,
            required: true,
        },
        notifications: {
            type: Array,
            required: false,
            default: () => []
        },
        labels: {
            type: Object,
            required: true,
        },
        forceOpen: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const store = useStore()
        const currentLocale = ref('da-DK');
        const showNotifications = ref(false);

        const open = computed(() => {
            if (props.forceOpen) return true;
            return store.getters["menu/isLoginOverlayOpen"];
        });

        const getNotificationBody = (notification) => {
            return notification.bodies[currentLocale.value]?.body || '';
        };

        const visibleNotifications = computed(() => {
            if (!showNotifications.value) return [];
            return props.notifications.filter(notification => getNotificationBody(notification));
        });

        const onOverlayEntered = () => {
            showNotifications.value = true;
        };

        const onOverlayLeft = () => {
            showNotifications.value = false;
        };

        const closeSubmenu = () => {
            showNotifications.value = false;
            handleCloseSubmenu(store)
        };

        // Navigation methods - use imported functions from menu-key-helper
        const navigateForwardInSubmenu = (e) => {
            handleNavigateForwardInSubmenu(e)
        };

        const navigateBackInSubmenu = (e) => {
            handleNavigateBackInSubmenu(e)
        };

        const navigateToPreviousListInSubmenu = (e) => {
            handleNavigateToPreviousListInSubmenu(e)
        };

        const navigateToNextListInSubmenu = (e) => {
            handleNavigateToNextListInSubmenu(e)
        };

        const footerTitle = computed(() => {
            return props.labels[currentLocale.value]?.footerTitle || '';
        });

        const altText = computed(() => {
            return props.labels[currentLocale.value]?.altText || '';
        });

        const languageBarAriaLabel = computed(() => {
            return props.labels[currentLocale.value]?.languageBarAriaLabel || '';
        });

        const quickLinksAriaLabel = computed(() => {
            return props.labels[currentLocale.value]?.quickLinksAriaLabel || '';
        });

        const footerAriaLabel = computed(() => {
            return props.labels[currentLocale.value]?.footerAriaLabel || '';
        });

        const notificationsAriaLabel = computed(() => {
            return props.labels[currentLocale.value]?.notificationsAriaLabel || '';
        });

        const getCardHeadline = (card) => {
            return card.labels?.[currentLocale.value]?.headline || '';
        };

        const getCardFooterText = (card) => {
            return card.labels?.[currentLocale.value]?.footerText || '';
        };

        const getCardHref = (card) => {
            return card.labels?.[currentLocale.value]?.href || '#';
        };

        const getFooterItemLabel = (item) => {
            const label = item.labels?.[currentLocale.value]?.label || '';

            const parser = new DOMParser();
            const doc = parser.parseFromString(label, 'text/html');
            const pTag = doc.querySelector('p');

            return pTag?.innerHTML || label;
        };

        const getFooterItemHref = (item) => {
            return item.labels?.[currentLocale.value]?.href || '#';
        };

        const getLanguageTitle = (languageItem) => {
            return languageItem.titles?.[currentLocale.value] || languageItem.languageCode;
        };

        const switchLanguage = (locale) => {
            currentLocale.value = locale;
        };

        const getNotificationTypeLabel = (notification) => {
            return notification.typeLabels?.[currentLocale.value] || '';
        };

        const getLanguageSwitchAriaLabel = (language) => {
            const code = currentLocale.value;
            const title = getLanguageTitle(language);
            if (code === 'da-DK') return `Skift til ${title}`;
            if (code === 'en-US') return `Switch to ${title}`;

            return `Switch to ${title}`;
        };

        const isTelLink = (item) => {
            return item.href && item.href.includes('tel:');
        };

        return {
            open,
            currentLocale,
            showNotifications,
            visibleNotifications,
            onOverlayEntered,
            onOverlayLeft,
            closeSubmenu,
            navigateForwardInSubmenu,
            navigateBackInSubmenu,
            navigateToPreviousListInSubmenu,
            navigateToNextListInSubmenu,
            footerTitle,
            altText,
            languageBarAriaLabel,
            quickLinksAriaLabel,
            footerAriaLabel,
            notificationsAriaLabel,
            getCardHeadline,
            getCardFooterText,
            getCardHref,
            getFooterItemLabel,
            getFooterItemHref,
            getLanguageTitle,
            switchLanguage,
            getNotificationBody,
            getNotificationTypeLabel,
            getLanguageSwitchAriaLabel,
            isTelLink
        }
    },
}
